import { Navigate, Outlet, Route } from "react-router";

import { queryClient } from "@/providers/QueryClient";
import { loadSearchTablePreferences } from "@/shared/domain/config/loaders/loadSearchTablePreferences";
import { QueryTarget } from "@/shared/domain/query";
import { makeViewsLoader } from "@/shared/domain/views/loaders/loadViews";
import { Goto } from "@/shared/navigation";
import { composeLoaders } from "@/shared/routing/composeLoaders";
import { makeFeatureFlagRedirectLoader } from "@/shared/routing/featureFlagRoute";
import { InheritOutletContext } from "@/shared/routing/InheritOutletContext";
import { RedirectToDefaultTab } from "@/shared/routing/RedirectToDefaultTab";

import { DeviceType, DeviceTypeTab } from "./DeviceType";
import { DeviceTypeConfigurations } from "./DeviceTypeConfigurations";
import { DeviceTypeDetails } from "./DeviceTypeDetails";
import { DeviceTypeDevices } from "./DeviceTypeDevices";
import { DeviceTypes } from "./DeviceTypes";

/**
 * Route tree for `/device-types`, mounted under that prefix by the central
 * router. Every page requires the `release-asset-store` feature flag, which
 * the loader of one pathless parent route checks; an org without the flag is
 * redirected to the devices page.
 */
export const DeviceTypesRoutes = () => [
  <Route
    key="asset-store-gate"
    loader={makeFeatureFlagRedirectLoader(queryClient, {
      flagName: "release-asset-store",
      redirectTo: Goto.route.Devices,
    })}
    element={<Outlet />}
  >
    <Route index element={<DeviceTypes />} />
    <Route path=":deviceTypeId" element={<DeviceType />}>
      <Route index element={<Navigate to={DeviceTypeTab.Devices} replace />} />
      {/*
        The devices grid reads the saved column order and page size only when
        it mounts, and applies a `?view=` link before its first query, so this
        route loads the table preferences and saved views before the tab
        renders, as `DevicesRoutes` does for the devices page.
      */}
      <Route
        loader={composeLoaders(
          loadSearchTablePreferences(
            queryClient,
            "device_search_table_preferences",
          ),
          makeViewsLoader(queryClient, { target: QueryTarget.Devices }),
        )}
        element={<InheritOutletContext />}
      >
        <Route path={DeviceTypeTab.Devices} element={<DeviceTypeDevices />} />
      </Route>
      <Route path={DeviceTypeTab.Details} element={<DeviceTypeDetails />} />
      {/* The path after `configurations/` is the folder open in the browser. */}
      <Route
        path={`${DeviceTypeTab.Configurations}/*`}
        element={<DeviceTypeConfigurations />}
      />
      <Route
        path="*"
        element={
          <RedirectToDefaultTab
            buildPath={({ deviceTypeId }) =>
              Goto.createDeviceTypeRoute(
                deviceTypeId ?? "",
                DeviceTypeTab.Devices,
              )
            }
          />
        }
      />
    </Route>
  </Route>,
];
